import React, { useEffect, useMemo, useRef, useState } from "react";
import $ from "jquery";
import { gsap } from "gsap";
import ApiURl from "../../controllers/Api";

function AddProduct() {
  // ===== Data =====
  const [catData, setCatData] = useState([]);
  const [sizeOptions, setSizeOptions] = useState([]);
  const [purityOptions, setPurityOptions] = useState([]);

  // ===== Form fields =====
  const [productName, setProductName] = useState("");
  const [productPrice, setProductPrice] = useState(""); // Default/base price
  const [description, setDescription] = useState("");
  const [stockQuantity, setStockQuantity] = useState("");

  const [currentCategoryId, setCurrentCategoryId] = useState("");
  const [categorySizeGroupId, setCategorySizeGroupId] = useState("");

  // ===== Multiple images (up to 5) =====
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const fileInputRef = useRef(null);

  // ===== Sizes + pricing + discounts =====
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [sizePrices, setSizePrices] = useState({}); // { [size]: number }
  const [selectedPurities, setSelectedPurities] = useState([]);
  const [purityPrices, setPurityPrices] = useState({});

  // SINGLE discount (buy 1) + NEW schedule
  // { [size]: { type: 'amount'|'percentage', value: number|string, start?: string, end?: string } }
  const [singleDiscounts, setSingleDiscounts] = useState({});

  // Bulk quantity discounts (min quantity based)
  const [minQuantities, setMinQuantities] = useState({}); // { [size]: number }
  const [bulkDiscountValues, setBulkDiscountValues] = useState({}); // { [size]: number } discount value
  const [bulkDiscountTypes, setBulkDiscountTypes] = useState({}); // { [size]: 'amount' | 'percentage' }

  // NEW: bulk schedule inputs (builder)
  const [bulkDiscountStarts, setBulkDiscountStarts] = useState({}); // { [size]: datetime-local string }
  const [bulkDiscountEnds, setBulkDiscountEnds] = useState({}); // { [size]: datetime-local string }

  // Stored bulk list: NEW includes start/end
  // { [size]: [{minQuantity, discountValue, discountType, start?, end?}] }
  const [bulkDiscounts, setBulkDiscounts] = useState({});

  // ===== UI state =====
  const [loadingCats, setLoadingCats] = useState(false);
  const [loadingSizes, setLoadingSizes] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [toast, setToast] = useState({ show: false, type: "info", msg: "" });

  // ===== GSAP Refs =====
  const pageRef = useRef(null);
  const leftCardRef = useRef(null);
  const rightCardRef = useRef(null);
  const tableWrapRef = useRef(null);
  const previewRef = useRef(null);

  // ===== Helpers =====
  const showToast = (type, msg) => {
    setToast({ show: true, type, msg });
    gsap.fromTo(
      ".toast-anim",
      { y: 12, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.25, ease: "power2.out" }
    );
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(
      () => setToast((t) => ({ ...t, show: false })),
      2600
    );
  };

  const fmtINR = (n) => {
    const v = Number(n);
    if (Number.isNaN(v)) return "";
    return v.toLocaleString("en-IN");
  };

  const isSelected = (size) => selectedSizes.includes(size);
  const isPuritySelected = (id) => selectedPurities.includes(String(id));

  // NEW: validate windows (start/end)
  const isValidWindow = (start, end) => {
  // If both are empty, it's valid (no schedule)
  if (!start && !end) return true;
  // If one is provided but not both, it's invalid
  if ((start && !end) || (!start && end)) return false;
  // If both provided, end must be after start
  return new Date(end).getTime() > new Date(start).getTime();
};

  const requiredBaseFieldsValid = useMemo(() => {
    return (
      productName.trim() &&
      description.trim() &&
      String(stockQuantity).trim() &&
      currentCategoryId &&
      categorySizeGroupId &&
      imageFiles.length > 0
    );
  }, [
    productName,
    description,
    stockQuantity,
    currentCategoryId,
    categorySizeGroupId,
    imageFiles,
  ]);

  // ===== Intro animation =====
  useEffect(() => {
    if (!pageRef.current) return;

    gsap.set([leftCardRef.current, rightCardRef.current], { opacity: 0, y: 18 });
    gsap.to(leftCardRef.current, {
      opacity: 1,
      y: 0,
      duration: 0.5,
      ease: "power3.out",
      delay: 0.05,
    });
    gsap.to(rightCardRef.current, {
      opacity: 1,
      y: 0,
      duration: 0.5,
      ease: "power3.out",
      delay: 0.12,
    });
  }, []);

  // ===== Fetch initial categories =====
  useEffect(() => {
    let isMounted = true;
    const fetchCategories = async () => {
      try {
        setLoadingCats(true);
        const response = await $.getJSON(`${ApiURl}/getMainCategory.php`);
        if (!isMounted) return;
        setCatData(response?.data || []);
      } catch (error) {
        console.error("Error fetching category data:", error);
        showToast("error", "Failed to load categories.");
      } finally {
        if (isMounted) setLoadingCats(false);
      }
    };
    fetchCategories();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    $.getJSON(`${ApiURl}/getPurities.php?active_only=1`)
      .then((response) => {
        if (isMounted) setPurityOptions(response?.purities || []);
      })
      .catch((error) => {
        console.error("Error fetching purity data:", error);
        showToast("error", "Failed to load purities.");
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const fetchSizes = async (categoryId) => {
    if (!categoryId) {
      setSizeOptions([]);
      return;
    }
    try {
      setLoadingSizes(true);
      const response = await $.getJSON(
        `${ApiURl}/getSizes.php?sub_category_id=${categoryId}`
      );
      if (response?.success) {
        const seenSizes = new Set();
        const uniqueSizes = (response.data || []).filter((row) => {
          const key = String(row?.size || "").trim().toLowerCase();
          if (!key || seenSizes.has(key)) return false;
          seenSizes.add(key);
          return true;
        });
        setSizeOptions(uniqueSizes);
      }
      else {
        setSizeOptions([]);
        console.error("Error fetching sizes:", response?.message);
      }
    } catch (error) {
      console.error("Error fetching size data:", error);
      showToast("error", "Failed to load sizes.");
    } finally {
      setLoadingSizes(false);
    }
  };

  useEffect(() => {
    // Sizes are now tied to the selected category. The legacy API key is kept for PHP compatibility.
    if (categorySizeGroupId) {
      fetchSizes(categorySizeGroupId);

      setSelectedSizes([]);
      setSizePrices({});
      setSingleDiscounts({});
      setMinQuantities({});
      setBulkDiscountValues({});
      setBulkDiscountTypes({});
      setBulkDiscountStarts({});
      setBulkDiscountEnds({});
      setBulkDiscounts({});
    } else {
      setSizeOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categorySizeGroupId]);

  // ===== Handlers =====
  const handleCategoryChange = (e) => {
    const selectedCategoryId = e.target.value;
    setCurrentCategoryId(selectedCategoryId);

    // Reset dependent selections
    setCategorySizeGroupId(selectedCategoryId);
  };

  // ===== Multiple image upload handler =====
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const limited = files.slice(0, 5);
    if (files.length > 5) {
      showToast(
        "info",
        "You can upload a maximum of 5 photos. Extra files were ignored."
      );
    }

    // Revoke old previews
    imagePreviews.forEach((u) => URL.revokeObjectURL(u));

    setImageFiles(limited);
    const urls = limited.map((f) => URL.createObjectURL(f));
    setImagePreviews(urls);

    // GSAP preview animation
    if (previewRef.current) {
      gsap.fromTo(
        previewRef.current,
        { opacity: 0, y: 10, scale: 0.98 },
        { opacity: 1, y: 0, scale: 1, duration: 0.35, ease: "power3.out" }
      );
    }
  };

  const removeOneImage = (index) => {
    setImageFiles((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });

    setImagePreviews((prev) => {
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      if (removed) URL.revokeObjectURL(removed);
      return next;
    });

    // If no images remain, clear file input value so user can re-select same files
    setTimeout(() => {
      if (fileInputRef.current && imageFiles.length <= 1) {
        fileInputRef.current.value = "";
      }
    }, 0);
  };

  const removeAllImages = () => {
    imagePreviews.forEach((u) => URL.revokeObjectURL(u));
    setImageFiles([]);
    setImagePreviews([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
    showToast("info", "Images removed.");
  };

  const handleSizeCheckboxChange = (e, size) => {
    if (e.target.checked) {
      setSelectedSizes((prev) => (prev.includes(size) ? prev : [...prev, size]));

      // animate row highlight
      gsap.fromTo(
        `.size-row-${CSS.escape(String(size))}`,
        { backgroundColor: "rgba(179, 144, 99, 0.08)" },
        { backgroundColor: "rgba(0,0,0,0)", duration: 0.9, ease: "power2.out" }
      );
    } else {
      setSelectedSizes((prev) => prev.filter((s) => s !== size));

      setSizePrices((prev) => {
        const copy = { ...prev };
        delete copy[size];
        return copy;
      });
      setSingleDiscounts((prev) => {
        const copy = { ...prev };
        delete copy[size];
        return copy;
      });
      setMinQuantities((prev) => {
        const copy = { ...prev };
        delete copy[size];
        return copy;
      });
      setBulkDiscountValues((prev) => {
        const copy = { ...prev };
        delete copy[size];
        return copy;
      });
      setBulkDiscountTypes((prev) => {
        const copy = { ...prev };
        delete copy[size];
        return copy;
      });
      setBulkDiscountStarts((prev) => {
        const copy = { ...prev };
        delete copy[size];
        return copy;
      });
      setBulkDiscountEnds((prev) => {
        const copy = { ...prev };
        delete copy[size];
        return copy;
      });
      setBulkDiscounts((prev) => {
        const copy = { ...prev };
        delete copy[size];
        return copy;
      });
    }
  };

  const handlePurityCheckboxChange = (e, purity) => {
    const id = String(purity.id);
    if (e.target.checked) {
      setSelectedPurities((prev) => (prev.includes(id) ? prev : [...prev, id]));
    } else {
      setSelectedPurities((prev) => prev.filter((item) => item !== id));
      setPurityPrices((prev) => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
    }
  };

  const handlePurityPriceChange = (e, purityId) => {
    const value = e.target.value;
    setPurityPrices((prev) => ({ ...prev, [String(purityId)]: value }));
  };

  const handleSizePriceChange = (e, size) => {
    const value = e.target.value;
    if (value === "") {
      setSizePrices((prev) => ({ ...prev, [size]: "" }));
      return;
    }
    const newPrice = Number(value);
    if (!Number.isNaN(newPrice) && newPrice >= 0) {
      setSizePrices((prev) => ({ ...prev, [size]: newPrice }));
    }
  };

  // ===== Single discount handlers (UPDATED: start/end) =====
  const handleSingleDiscountTypeChange = (e, size) => {
    const type = e.target.value;
    setSingleDiscounts((prev) => ({
      ...prev,
      [size]: { ...prev[size], type },
    }));
  };

 const handleSingleDiscountValueChange = (e, size) => {
  const value = e.target.value;
  if (value === "") {
    setSingleDiscounts((prev) => ({
      ...prev,
      [size]: { ...prev[size], value: "" },
    }));
    return;
  }
  const num = Number(value);
  if (!Number.isNaN(num) && num >= 0) {
    // Store the value - validation will happen on submit
    setSingleDiscounts((prev) => ({
      ...prev,
      [size]: { ...prev[size], value: num },
    }));
  }
};

  // NEW: Single schedule handlers
  const handleSingleDiscountStartChange = (e, size) => {
    const start = e.target.value;
    setSingleDiscounts((prev) => ({
      ...prev,
      [size]: { ...prev[size], start },
    }));
  };

  const handleSingleDiscountEndChange = (e, size) => {
    const end = e.target.value;
    setSingleDiscounts((prev) => ({
      ...prev,
      [size]: { ...prev[size], end },
    }));
  };

  // ===== Bulk discount handlers =====
  const handleMinQuantityChange = (e, size) => {
    const value = e.target.value;
    if (value === "") {
      setMinQuantities((prev) => ({ ...prev, [size]: "" }));
      return;
    }
    const n = Number(value);
    if (!Number.isNaN(n) && n >= 1) {
      setMinQuantities((prev) => ({ ...prev, [size]: parseInt(value, 10) }));
    }
  };

  const handleBulkDiscountValueChange = (e, size) => {
    const value = e.target.value;
    if (value === "") {
      setBulkDiscountValues((prev) => ({ ...prev, [size]: "" }));
      return;
    }
    const n = Number(value);
    if (!Number.isNaN(n) && n >= 0) {
      setBulkDiscountValues((prev) => ({ ...prev, [size]: n }));
    }
  };

  const handleBulkDiscountTypeChange = (e, size) => {
    setBulkDiscountTypes((prev) => ({ ...prev, [size]: e.target.value }));
  };

  // NEW: bulk schedule handlers (builder inputs)
  const handleBulkDiscountStartChange = (e, size) => {
    setBulkDiscountStarts((prev) => ({ ...prev, [size]: e.target.value }));
  };

  const handleBulkDiscountEndChange = (e, size) => {
    setBulkDiscountEnds((prev) => ({ ...prev, [size]: e.target.value }));
  };

  const handleAddBulkDiscount = (size) => {
  const currentMinQuantity = minQuantities[size];
  const currentDiscountValue = bulkDiscountValues[size];
  const currentDiscountType = bulkDiscountTypes[size] || "amount";

  const start = bulkDiscountStarts[size] || "";
  const end = bulkDiscountEnds[size] || "";

  if (!isSelected(size)) return;

  // Only validate if they're trying to add a discount
  if (!currentMinQuantity || !currentDiscountValue) {
    showToast("info", "Enter min quantity and discount value first.");
    return;
  }

  // Validate discount based on type
  if (currentDiscountType === "percentage") {
    if (currentDiscountValue > 100) {
      showToast("error", "Percentage discount cannot exceed 100%.");
      return;
    }
    if (currentDiscountValue <= 0) {
      showToast("error", "Please enter a valid percentage (1-100).");
      return;
    }
  } else {
    if (currentDiscountValue <= 0) {
      showToast("error", "Please enter a valid discount amount.");
      return;
    }
  }

  // Validate schedule window only if they provided dates
  if ((start || end) && !isValidWindow(start, end)) {
    showToast("error", `Bulk discount (${size}): If providing dates, End must be after Start.`);
    return;
  }

  // Rest of the function remains the same...
  setBulkDiscounts((prev) => {
    const existing = prev[size] || [];
    const newDiscount = {
      minQuantity: currentMinQuantity,
      discountValue: currentDiscountValue,
      discountType: currentDiscountType,
      start,
      end,
    };
    return { ...prev, [size]: [...existing, newDiscount] };
  });

  // Clear inputs
  setMinQuantities((prev) => ({ ...prev, [size]: "" }));
  setBulkDiscountValues((prev) => ({ ...prev, [size]: "" }));
  setBulkDiscountTypes((prev) => ({ ...prev, [size]: "amount" }));
  setBulkDiscountStarts((prev) => ({ ...prev, [size]: "" }));
  setBulkDiscountEnds((prev) => ({ ...prev, [size]: "" }));

  gsap.fromTo(
    `.bulk-disc-badge-${CSS.escape(String(size))}`,
    { scale: 0.9, opacity: 0 },
    { scale: 1, opacity: 1, duration: 0.25, ease: "power2.out" }
  );
};

  const handleRemoveBulkDiscount = (size, index) => {
    setBulkDiscounts((prev) => {
      const next = { ...prev };
      if (next[size]) {
        next[size] = next[size].filter((_, i) => i !== index);
        if (next[size].length === 0) delete next[size];
      }
      return next;
    });
  };

  const handleRemoveSingleDiscount = (size) => {
    setSingleDiscounts((prev) => {
      const copy = { ...prev };
      delete copy[size];
      return copy;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!requiredBaseFieldsValid) {
      showToast("error", "Please fill all required fields and upload at least one image.");
      return;
    }

    if (selectedSizes.length === 0 && selectedPurities.length === 0 && Number(productPrice) <= 0) {
      showToast("error", "Add at least one size price, purity price, or base price.");
      return;
    }

    // Validate sizes only when selected
    for (const s of selectedSizes) {
      const p = sizePrices[s];
      if (p === "" || p === undefined || Number(p) <= 0) {
        showToast("error", `Enter a valid price for size ${s}.`);
        return;
      }
    }

    for (const id of selectedPurities) {
      const selected = purityOptions.find((purity) => String(purity.id) === String(id));
      const p = purityPrices[id];
      if (p === "" || p === undefined || Number(p) <= 0) {
        showToast("error", `Enter a valid price for purity ${selected?.name || id}.`);
        return;
      }
    }

    // NEW: Validate single discount windows (only if a discount value is set)
    // NEW: Validate single discount windows (only if a discount value is set)
for (const s of selectedSizes) {
  const d = singleDiscounts[s];
  if (d && d.value) {  // ← Only validate if they actually entered a value
    const start = d.start || "";
    const end = d.end || "";
    if (!isValidWindow(start, end)) {
      showToast("error", `Single discount (${s}): Select both Start & End and End must be after Start.`);
      return;
    }
  }
}

    setSubmitting(true);

    const formData = new FormData();
    formData.append("productName", productName);
    formData.append("description", description);
    formData.append("currentCategoryId", parseInt(currentCategoryId, 10));
    formData.append("currentSubCategoryId", parseInt(categorySizeGroupId, 10));
    formData.append("stock_quantity", stockQuantity);
    if (productPrice) formData.append("simple_price", productPrice);

    // Append up to 5 images
    imageFiles.forEach((file) => {
      formData.append("images[]", file);
    });

    // Append sizes and their prices
    selectedSizes.forEach((size) => {
      formData.append("sizes[]", size);
      formData.append(`price_${size}`, sizePrices[size] || 0);
    });

    selectedPurities.forEach((id) => {
      const selected = purityOptions.find((purity) => String(purity.id) === String(id));
      formData.append("purities[]", id);
      formData.append(`purity_prices[${id}]`, purityPrices[id] || 0);
      formData.append(`purity_names[${id}]`, selected?.name || "");
    });

   // Append single quantity discounts + start/end (only if value exists)
selectedSizes.forEach((size) => {
  if (singleDiscounts[size] && singleDiscounts[size].value) {  // ← Check if value exists
    formData.append(`single_discounts[${size}][type]`, singleDiscounts[size].type || "amount");
    formData.append(`single_discounts[${size}][value]`, singleDiscounts[size].value);

    if (singleDiscounts[size].start) {
      formData.append(`single_discounts[${size}][start]`, singleDiscounts[size].start);
    }
    if (singleDiscounts[size].end) {
      formData.append(`single_discounts[${size}][end]`, singleDiscounts[size].end);
    }
  }
});

// Append bulk discounts + start/end (only if they exist)
selectedSizes.forEach((size) => {
  if (bulkDiscounts[size] && bulkDiscounts[size].length > 0) {  // ← Check if bulk discounts exist
    bulkDiscounts[size].forEach((discount, index) => {
      formData.append(`bulk_discounts[${size}][${index}][min_quantity]`, discount.minQuantity);
      formData.append(`bulk_discounts[${size}][${index}][discount_value]`, discount.discountValue);
      formData.append(`bulk_discounts[${size}][${index}][discount_type]`, discount.discountType);

      if (discount.start) {
        formData.append(`bulk_discounts[${size}][${index}][start]`, discount.start);
      }
      if (discount.end) {
        formData.append(`bulk_discounts[${size}][${index}][end]`, discount.end);
      }
    });
  }
});

    $.ajax({
      url: `${ApiURl}/createProduct.php`,
      type: "POST",
      data: formData,
      processData: false,
      contentType: false,
      success: function (response) {
        setSubmitting(false);

        // Debug log to see response
        console.log("Server Response:", response);

        if (response?.success) {
          showToast("success", "Product added successfully.");

          // Reset form
          resetForm();

          // Success animation
          gsap.fromTo(
            leftCardRef.current,
            { boxShadow: "0 0 0 rgba(0,0,0,0)" },
            { boxShadow: "0 0 0 6px rgba(16,185,129,0.15)", duration: 0.5, ease: "power2.out" }
          );
        } else {
          showToast("error", response?.message || "Failed to add product.");
          console.error("Server error:", response);
        }
      },
      error: function (xhr, status, error) {
        setSubmitting(false);
        console.error("AJAX Error:", xhr.responseText);
        showToast("error", "Server error while creating product. Check console for details.");
      },
    });
  };

  // Add this reset function
  const resetForm = () => {
    setProductName("");
    setProductPrice("");
    setDescription("");
    setStockQuantity("");
    setCurrentCategoryId("");
    setCategorySizeGroupId("");
    setSizeOptions([]);
    setSelectedSizes([]);
    setSizePrices({});
    setSelectedPurities([]);
    setPurityPrices({});
    setSingleDiscounts({});
    setMinQuantities({});
    setBulkDiscountValues({});
    setBulkDiscountTypes({});
    setBulkDiscountStarts({});
    setBulkDiscountEnds({});
    setBulkDiscounts({});

    // Clear images
    imagePreviews.forEach((u) => URL.revokeObjectURL(u));
    setImageFiles([]);
    setImagePreviews([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ===== Derived: Discount summaries =====
  const bulkDiscountSummary = useMemo(() => {
    const items = [];
    Object.entries(bulkDiscounts).forEach(([size, list]) => {
      list.forEach((d, idx) => {
        items.push({ size, idx, ...d });
      });
    });
    return items;
  }, [bulkDiscounts]);

  // Format discount display
  const formatDiscount = (discount) => {
    if (discount.discountType === "percentage") {
      return `${discount.discountValue}% off`;
    } else {
      return `₹${fmtINR(discount.discountValue)} off`;
    }
  };

  const formatSingleDiscount = (size) => {
    const disc = singleDiscounts[size];
    if (!disc || !disc.value) return null;
    if (disc.type === "percentage") {
      return `${disc.value}% off`;
    } else {
      return `₹${fmtINR(disc.value)} off`;
    }
  };

  return (
    <div ref={pageRef} className="add-product-luxury min-h-screen w-full overflow-x-hidden bg-[#F7EFE7] font-['Kanit',sans-serif] text-[#2B0000]">
      {/* Toast */}
      {toast.show && (
        <div className="toast-anim fixed bottom-5 right-5 z-[9999] max-w-[calc(100vw-24px)]">
          <div
            className={[
              "rounded-2xl px-5 py-3.5 shadow-[0_18px_45px_rgba(43,0,0,0.2)] border text-sm backdrop-blur-md",
              toast.type === "success"
                ? "bg-[#edf8f1] border-emerald-200 text-emerald-900"
                : toast.type === "error"
                ? "bg-rose-50 border-rose-200 text-rose-900"
                : "bg-slate-50 border-slate-200 text-slate-900",
            ].join(" ")}
          >
            {toast.msg}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Page Header */}
        <div className="relative overflow-hidden border-b border-[#d4af37]/20 bg-[radial-gradient(circle_at_top_left,rgba(212,175,55,0.18),transparent_34%),linear-gradient(135deg,#2B0000,#170000)] px-4 pb-8 pt-7 text-white sm:px-6 lg:px-8">
          <div className="mx-auto flex max-w-[1500px] flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="font-serif text-3xl font-medium tracking-tight text-[#F7E5B3] sm:text-4xl lg:text-5xl">
                Add Product
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-white/65">
                Create a new product with required sizes and optional discounts.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-white/55">
                {loadingCats || loadingSizes ? "Loading data..." : "Ready"}
              </span>
              <span
                className={[
                  "inline-flex items-center rounded-full px-3 py-1.5 text-xs font-medium border backdrop-blur",
                  requiredBaseFieldsValid ? "bg-emerald-500/15 border-emerald-300/30 text-emerald-100" : "bg-[#d4af37]/12 border-[#d4af37]/30 text-[#f7e5b3]",
                ].join(" ")}
              >
                {requiredBaseFieldsValid ? "Base fields complete" : "Complete required fields"}
              </span>
            </div>
          </div>
        </div>

        {/* Layout */}
        <div className="px-3 py-5 sm:px-5 sm:py-7 lg:px-7">
          <div className="mx-auto grid max-w-[1500px] grid-cols-1 gap-6 xl:grid-cols-12">
            {/* LEFT: Main Form */}
            <section
              ref={leftCardRef}
              className="overflow-hidden rounded-[24px] border border-[#d4af37]/20 bg-[#fffaf5] shadow-[0_24px_70px_rgba(43,0,0,0.10)] xl:col-span-8"
            >
              <div className="border-b border-[#d4af37]/18 bg-[linear-gradient(135deg,#2B0000,#4A090B)] px-5 py-5 text-white sm:px-6">
                <h2 className="font-serif text-xl font-medium text-[#F7E5B3]">Product Details</h2>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-white/65">
                  Fill the product information and configure sizes. Discounts are optional.
                </p>
              </div>

              <div className="p-4 sm:p-6">
                <div className="grid gap-5 sm:grid-cols-2">
                  {/* Product Name */}
                  <div className="sm:col-span-2">
                    <label className="mb-2 block text-sm font-medium text-[#3A1715]">
                      Product Name <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      className="w-full rounded-xl border border-[#dbc9bb] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition placeholder:text-[#8b7770] focus:border-[#d4af37] focus:ring-4 focus:ring-[#d4af37]/10"
                      placeholder="Type product name"
                      required
                    />
                  </div>

                  {/* Category */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-[#3A1715]">
                      Category <span className="text-rose-500">*</span>
                    </label>
                    <select
                      value={currentCategoryId}
                      onChange={handleCategoryChange}
                      className="w-full rounded-xl border border-[#dbc9bb] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition focus:border-[#d4af37] focus:ring-4 focus:ring-[#d4af37]/10"
                      required
                    >
                      <option value="" hidden>
                        {loadingCats ? "Loading..." : "Select category"}
                      </option>
                      {catData.map((row) => (
                        <option key={row.id} value={row.id}>
                          {row.category_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Stock */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-[#3A1715]">
                      Stock Quantity <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={stockQuantity}
                      onChange={(e) => setStockQuantity(e.target.value)}
                      className="w-full rounded-xl border border-[#dbc9bb] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition placeholder:text-[#8b7770] focus:border-[#d4af37] focus:ring-4 focus:ring-[#d4af37]/10"
                      placeholder="Enter stock quantity"
                      min="0"
                      required
                    />
                  </div>

                  {/* Base Price */}
                  <div className="sm:col-span-2">
                    <label className="mb-2 block text-sm font-medium text-[#3A1715]">
                      Base Price (Optional)
                    </label>
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="flex w-full items-center rounded-xl border border-[#dbc9bb] bg-white px-4 py-3 text-sm shadow-sm transition focus-within:border-[#d4af37] focus-within:ring-4 focus-within:ring-[#d4af37]/10">
                        <span className="text-slate-500 mr-2">₹</span>
                        <input
                          type="number"
                          value={productPrice}
                          onChange={(e) => setProductPrice(e.target.value)}
                          className="w-full bg-transparent text-[#2B0000] outline-none placeholder:text-[#8b7770]"
                          placeholder="Default/base price (if applicable)"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <span className="hidden text-xs text-[#7B6962] sm:inline">
                        Size prices override base price.
                      </span>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="sm:col-span-2">
                    <label className="mb-2 block text-sm font-medium text-[#3A1715]">
                      Description <span className="text-rose-500">*</span>
                    </label>
                    <textarea
                      rows={4}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="w-full rounded-xl border border-[#dbc9bb] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition placeholder:text-[#8b7770] focus:border-[#d4af37] focus:ring-4 focus:ring-[#d4af37]/10"
                      placeholder="Type product description..."
                      required
                    />
                  </div>
                </div>

                {/* Purities */}
                <div className="mt-7 rounded-[20px] border border-[#d4af37]/25 bg-[linear-gradient(145deg,#fffaf5,#f7ecdf)] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] sm:p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-serif text-lg font-medium text-[#2B0000]">Purity Prices</h3>
                      <p className="mt-2 max-w-2xl text-sm leading-6 text-white/65">
                        Optional. Select purities like 18K or 22K and set separate prices for this product.
                      </p>
                    </div>
                    <span className="text-xs text-white/55">{purityOptions.length} purity option(s)</span>
                  </div>

                  <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {purityOptions.map((purity) => {
                      const selected = isPuritySelected(purity.id);
                      return (
                        <label
                          key={purity.id}
                          className="rounded-xl border border-[#e6d7ca] bg-white/90 p-3 shadow-[0_8px_22px_rgba(43,0,0,0.05)] transition hover:-translate-y-0.5 hover:border-[#d4af37]/45 hover:shadow-[0_14px_30px_rgba(43,0,0,0.09)]"
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={(e) => handlePurityCheckboxChange(e, purity)}
                              className="h-4 w-4 accent-[#2B0000]"
                            />
                            <span className="font-semibold text-[#2B0000]">{purity.name}</span>
                          </div>
                          {selected && (
                            <div className="mt-3 flex items-center rounded-xl border border-[#dbc9bb] bg-white px-3 py-2.5 text-sm shadow-sm focus-within:border-[#d4af37] focus-within:ring-4 focus-within:ring-[#d4af37]/10">
                              <span className="mr-2 text-slate-500">₹</span>
                              <input
                                type="number"
                                value={purityPrices[String(purity.id)] ?? ""}
                                onChange={(e) => handlePurityPriceChange(e, purity.id)}
                                className="w-full bg-transparent outline-none"
                                placeholder={`${purity.name} price`}
                                min="0"
                                step="0.01"
                              />
                            </div>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Sizes */}
                <div className="mt-6">
                  <div className="flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row">
                    <div>
                      <h3 className="font-serif text-lg font-medium text-[#2B0000]">Sizes & Optional Discounts</h3>
                      <p className="mt-2 max-w-2xl text-sm leading-6 text-white/65">
                        Sizes are optional. Select sizes only when this product needs size-based pricing.
                      </p>
                    </div>
                    <div className="text-xs text-white/55">
                      {loadingSizes ? "Loading sizes..." : `${sizeOptions.length} size option(s)`}
                    </div>
                  </div>

                  <div
                    ref={tableWrapRef}
                    className="mt-4 overflow-hidden rounded-[20px] border border-[#d4af37]/20 bg-white shadow-[0_16px_45px_rgba(43,0,0,0.07)]"
                  >
                    <div className="overflow-x-auto">
                      {/* UPDATED: wider table now */}
                      <table className="min-w-[1900px] w-full">
                        <thead className="border-b border-[#d4af37]/20 bg-[#2B0000] text-white">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[#F7E5B3]">Select</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[#F7E5B3]">Size *</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[#F7E5B3]">Price (₹) *</th>

                            {/* UPDATED: Single now has Start/End */}
                            <th colSpan={4} className="bg-[#5a2a00] px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[#F7E5B3]">
                              Single Discount (Optional)
                            </th>

                            {/* UPDATED: Bulk now has Start/End */}
                            <th colSpan={6} className="bg-[#32195a] px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[#F7E5B3]">
                              Bulk Quantity Discount (Optional)
                            </th>
                          </tr>
                          <tr className="border-t border-white/10">
                            <th colSpan={3} className="px-4 py-2"></th>

                            {/* Single Discount sub-headers */}
                            <th className="bg-[#6d3604] px-4 py-2 text-left text-xs font-medium text-[#F7E5B3]">Type</th>
                            <th className="bg-[#6d3604] px-4 py-2 text-left text-xs font-medium text-[#F7E5B3]">Value</th>
                            <th className="bg-[#6d3604] px-4 py-2 text-left text-xs font-medium text-[#F7E5B3]">Start</th>
                            <th className="bg-[#6d3604] px-4 py-2 text-left text-xs font-medium text-[#F7E5B3]">End</th>

                            {/* Bulk Discount sub-headers */}
                            <th className="bg-[#412071] px-4 py-2 text-left text-xs font-medium text-[#F7E5B3]">Min Qty</th>
                            <th className="bg-[#412071] px-4 py-2 text-left text-xs font-medium text-[#F7E5B3]">Discount</th>
                            <th className="bg-[#412071] px-4 py-2 text-left text-xs font-medium text-[#F7E5B3]">Type</th>
                            <th className="bg-[#412071] px-4 py-2 text-left text-xs font-medium text-[#F7E5B3]">Start</th>
                            <th className="bg-[#412071] px-4 py-2 text-left text-xs font-medium text-[#F7E5B3]">End</th>
                            <th className="bg-[#412071] px-4 py-2 text-left text-xs font-medium text-[#F7E5B3]">Action</th>
                          </tr>
                        </thead>

                        <tbody className="divide-y divide-[#eadfd5]">
                          {sizeOptions.map((option, index) => {
                            const size = option.size;
                            const selected = isSelected(size);

                            const singleDisc = singleDiscounts[size] || { type: "amount", value: "", start: "", end: "" };

                            return (
                              <tr key={option.id || `${size}-${index}`} className={`size-row-${size} transition hover:bg-[#fff7ed] ${selected ? "bg-[#fffaf0]" : "bg-white"}`}>
                                <td className="px-4 py-3">
                                  <input
                                    type="checkbox"
                                    onChange={(e) => handleSizeCheckboxChange(e, size)}
                                    checked={selected}
                                    className="h-4 w-4 accent-[#2B0000]"
                                  />
                                </td>

                                <td className="px-4 py-3 text-sm font-medium text-slate-900">
                                  {size}
                                </td>

                                <td className="px-4 py-3">
                                  {selected ? (
                                    <div className="flex items-center rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm">
                                      <span className="text-slate-500 mr-2">₹</span>
                                      <input
                                        type="number"
                                        value={sizePrices[size] ?? ""}
                                        onChange={(e) => handleSizePriceChange(e, size)}
                                        className="w-24 bg-transparent text-[#2B0000] outline-none"
                                        placeholder="Price"
                                        min="0"
                                        step="0.01"
                                      />
                                    </div>
                                  ) : (
                                    <span className="text-xs text-slate-400">—</span>
                                  )}
                                </td>

                                {/* Single Discount - Type */}
                                <td className="px-4 py-3 bg-amber-50/30">
                                  {selected ? (
                                    <select
                                      value={singleDisc.type}
                                      onChange={(e) => handleSingleDiscountTypeChange(e, size)}
                                      className="w-20 rounded-xl border border-[#dbc9bb] bg-white px-3 py-2 text-sm outline-none focus:border-[#d4af37] focus:ring-4 focus:ring-[#d4af37]/10"
                                    >
                                      <option value="amount">₹</option>
                                      <option value="percentage">%</option>
                                    </select>
                                  ) : (
                                    <span className="text-xs text-slate-400">—</span>
                                  )}
                                </td>

                                {/* Single Discount - Value */}
                                <td className="px-4 py-3 bg-amber-50/30">
                                  {selected ? (
                                    <input
                                      type="number"
                                      value={singleDisc.value}
                                      onChange={(e) => handleSingleDiscountValueChange(e, size)}
                                      className="w-24 rounded-xl border border-[#dbc9bb] bg-white px-3 py-2 text-sm outline-none focus:border-[#d4af37] focus:ring-4 focus:ring-[#d4af37]/10"
                                      placeholder={singleDisc.type === "percentage" ? "%" : "₹"}
                                      min="0"
                                      step={singleDisc.type === "percentage" ? "1" : "0.01"}
                                    />
                                  ) : (
                                    <span className="text-xs text-slate-400">—</span>
                                  )}
                                </td>

                                {/* Single Discount - Start (NEW) */}
                                <td className="px-4 py-3 bg-amber-50/30">
                                  {selected ? (
                                    <input
                                      type="datetime-local"
                                      value={singleDisc.start || ""}
                                      onChange={(e) => handleSingleDiscountStartChange(e, size)}
                                      className="w-[190px] rounded-xl border border-[#dbc9bb] bg-white px-3 py-2 text-sm outline-none focus:border-[#d4af37] focus:ring-4 focus:ring-[#d4af37]/10"
                                    />
                                  ) : (
                                    <span className="text-xs text-slate-400">—</span>
                                  )}
                                </td>

                                {/* Single Discount - End (NEW) */}
                                <td className="px-4 py-3 bg-amber-50/30">
                                  {selected ? (
                                    <input
                                      type="datetime-local"
                                      value={singleDisc.end || ""}
                                      onChange={(e) => handleSingleDiscountEndChange(e, size)}
                                      className="w-[190px] rounded-xl border border-[#dbc9bb] bg-white px-3 py-2 text-sm outline-none focus:border-[#d4af37] focus:ring-4 focus:ring-[#d4af37]/10"
                                    />
                                  ) : (
                                    <span className="text-xs text-slate-400">—</span>
                                  )}
                                </td>

                                {/* Bulk Discount - Min Qty */}
                                <td className="px-4 py-3 bg-blue-50/30">
                                  {selected ? (
                                    <input
                                      type="number"
                                      value={minQuantities[size] ?? ""}
                                      onChange={(e) => handleMinQuantityChange(e, size)}
                                      className="w-20 rounded-xl border border-[#dbc9bb] bg-white px-3 py-2 text-sm outline-none focus:border-[#d4af37] focus:ring-4 focus:ring-[#d4af37]/10"
                                      placeholder="Min"
                                      min="1"
                                    />
                                  ) : (
                                    <span className="text-xs text-slate-400">—</span>
                                  )}
                                </td>

                                {/* Bulk Discount - Value */}
                                <td className="px-4 py-3 bg-blue-50/30">
                                  {selected ? (
                                    <input
                                      type="number"
                                      value={bulkDiscountValues[size] ?? ""}
                                      onChange={(e) => handleBulkDiscountValueChange(e, size)}
                                      className="w-24 rounded-xl border border-[#dbc9bb] bg-white px-3 py-2 text-sm outline-none focus:border-[#d4af37] focus:ring-4 focus:ring-[#d4af37]/10"
                                      placeholder="Value"
                                      min="0"
                                      step="0.01"
                                    />
                                  ) : (
                                    <span className="text-xs text-slate-400">—</span>
                                  )}
                                </td>

                                {/* Bulk Discount - Type */}
                                <td className="px-4 py-3 bg-blue-50/30">
                                  {selected ? (
                                    <select
                                      value={bulkDiscountTypes[size] || "amount"}
                                      onChange={(e) => handleBulkDiscountTypeChange(e, size)}
                                      className="w-20 rounded-xl border border-[#dbc9bb] bg-white px-3 py-2 text-sm outline-none focus:border-[#d4af37] focus:ring-4 focus:ring-[#d4af37]/10"
                                    >
                                      <option value="amount">₹</option>
                                      <option value="percentage">%</option>
                                    </select>
                                  ) : (
                                    <span className="text-xs text-slate-400">—</span>
                                  )}
                                </td>

                                {/* Bulk Discount - Start (NEW) */}
                                <td className="px-4 py-3 bg-blue-50/30">
                                  {selected ? (
                                    <input
                                      type="datetime-local"
                                      value={bulkDiscountStarts[size] || ""}
                                      onChange={(e) => handleBulkDiscountStartChange(e, size)}
                                      className="w-[190px] rounded-xl border border-[#dbc9bb] bg-white px-3 py-2 text-sm outline-none focus:border-[#d4af37] focus:ring-4 focus:ring-[#d4af37]/10"
                                    />
                                  ) : (
                                    <span className="text-xs text-slate-400">—</span>
                                  )}
                                </td>

                                {/* Bulk Discount - End (NEW) */}
                                <td className="px-4 py-3 bg-blue-50/30">
                                  {selected ? (
                                    <input
                                      type="datetime-local"
                                      value={bulkDiscountEnds[size] || ""}
                                      onChange={(e) => handleBulkDiscountEndChange(e, size)}
                                      className="w-[190px] rounded-xl border border-[#dbc9bb] bg-white px-3 py-2 text-sm outline-none focus:border-[#d4af37] focus:ring-4 focus:ring-[#d4af37]/10"
                                    />
                                  ) : (
                                    <span className="text-xs text-slate-400">—</span>
                                  )}
                                </td>

                                {/* Bulk Discount - Action */}
                                <td className="px-4 py-3 bg-blue-50/30">
                                  {selected ? (
                                    <button
                                      type="button"
                                      onClick={() => handleAddBulkDiscount(size)}
                                      className="inline-flex items-center justify-center rounded-xl border border-[#d4af37]/30 bg-[#2B0000] px-3 py-2 text-sm font-semibold text-[#F7E5B3] shadow-md transition hover:-translate-y-0.5 hover:bg-[#4A090B] active:scale-[0.98]"
                                    >
                                      Add Discount
                                    </button>
                                  ) : (
                                    <span className="text-xs text-slate-400">—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}

                          {!loadingSizes && sizeOptions.length === 0 && (
                            <tr>
                              <td colSpan={20} className="px-4 py-10 text-center text-sm text-slate-500">
                                Select a category to load sizes.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Selected summary */}
                    <div className="flex flex-col gap-2 border-t border-[#d4af37]/15 bg-[#fbf3ea] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="text-xs text-slate-600">
                        Selected sizes:{" "}
                        <span className="font-semibold text-[#2B0000]">
                          {selectedSizes.length ? selectedSizes.join(", ") : "None"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Single Discounts Summary (UPDATED: show schedule if present) */}
                  {Object.keys(singleDiscounts).length > 0 && (
                    <div className="mt-5">
                      <h4 className="text-sm font-semibold text-[#2B0000]">Single Quantity Discounts</h4>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {Object.entries(singleDiscounts).map(([size, disc]) => (
                          disc.value && (
                            <div
                              key={size}
                              className="inline-flex items-center gap-2 rounded-full border border-[#d4af37]/30 bg-[#fff7e6] px-3 py-1.5 text-xs text-[#4b332c] shadow-sm"
                            >
                              <span className="font-semibold text-[#2B0000]">{size}</span>
                              <span className="text-amber-600">Single:</span>
                              <span className="font-medium">
                                {disc.type === "percentage" ? `${disc.value}%` : `₹${fmtINR(disc.value)}`}
                              </span>
                              {(disc.start && disc.end) && (
                                <span className="text-slate-500">
                                  ({disc.start} → {disc.end})
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => handleRemoveSingleDiscount(size)}
                                className="ml-1 rounded-full px-2 py-0.5 text-rose-600 hover:bg-rose-50 border border-transparent hover:border-rose-200 transition"
                              >
                                Remove
                              </button>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Bulk Discounts Summary (UPDATED: show schedule if present) */}
                  {bulkDiscountSummary.length > 0 && (
                    <div className="mt-5">
                      <h4 className="text-sm font-semibold text-[#2B0000]">Bulk Quantity Discounts</h4>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {bulkDiscountSummary.map((d) => (
                          <div
                            key={`${d.size}-${d.idx}`}
                            className={`bulk-disc-badge-${d.size} inline-flex items-center gap-2 rounded-full border border-[#7a5aa7]/25 bg-[#f5efff] px-3 py-1.5 text-xs text-[#4b332c] shadow-sm`}
                          >
                            <span className="font-semibold text-[#2B0000]">{d.size}</span>
                            <span className="text-blue-600">Min:</span>
                            <span className="font-medium">{d.minQuantity}</span>
                            <span className="text-blue-600">Discount:</span>
                            <span className="font-medium">
                              {d.discountType === "percentage" ? `${d.discountValue}%` : `₹${fmtINR(d.discountValue)}`}
                            </span>
                            {(d.start && d.end) && (
                              <span className="text-slate-500">
                                ({d.start} → {d.end})
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveBulkDiscount(d.size, d.idx)}
                              className="ml-1 rounded-full px-2 py-0.5 text-rose-600 hover:bg-rose-50 border border-transparent hover:border-rose-200 transition"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Submit Bar */}
                <div className="mt-8 flex flex-col gap-3 border-t border-[#d4af37]/15 pt-6 sm:flex-row sm:items-center sm:justify-end">
                  <div className="text-xs text-slate-500 sm:mr-auto">
                    Fields marked <span className="text-rose-500">*</span> are required.
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className={[
                      "inline-flex items-center justify-center rounded-xl px-6 py-3.5 text-sm font-semibold transition shadow-[0_12px_30px_rgba(43,0,0,0.18)]",
                      submitting ? "bg-[#8d7d75] text-white cursor-not-allowed" : "bg-[#2B0000] text-[#F7E5B3] hover:bg-[#4A090B] hover:-translate-y-0.5 active:scale-[0.99]",
                    ].join(" ")}
                  >
                    {submitting ? "Saving..." : "Add Product"}
                  </button>
                </div>
              </div>
            </section>

            {/* RIGHT: Upload & Preview */}
            <section
              ref={rightCardRef}
              className="overflow-hidden rounded-[24px] border border-[#d4af37]/20 bg-[#fffaf5] shadow-[0_24px_70px_rgba(43,0,0,0.10)] xl:col-span-4 xl:sticky xl:top-20 xl:self-start"
            >
              <div className="border-b border-[#d4af37]/18 bg-[linear-gradient(135deg,#2B0000,#4A090B)] px-5 py-5 text-white sm:px-6">
                <h2 className="font-serif text-xl font-medium text-[#F7E5B3]">Media</h2>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-white/65">Upload up to 5 product images (max 2MB each).</p>
              </div>

              <div className="p-4 sm:p-6">
                <label
                  htmlFor="dropzone-file"
                  className="flex w-full cursor-pointer flex-col items-center justify-center rounded-[20px] border-2 border-dashed border-[#d4af37]/40 bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.09),transparent_70%)] p-8 text-center transition hover:border-[#d4af37] hover:bg-[#fff7e8]"
                >
                  <div className="flex flex-col items-center justify-center gap-2">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-[#d4af37]/30 bg-[#2B0000] text-[#F7E5B3] shadow-[0_10px_25px_rgba(43,0,0,0.16)]">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="text-slate-600">
                        <path d="M12 16V8M8 12h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <path d="M20 16.5V18a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <path d="M7 10.5 12 6l5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>

                    <p className="text-sm text-[#4a3732]">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-white/55">
                      PNG, JPG, GIF (Max 5 files)
                    </p>
                  </div>

                  <input
                    ref={fileInputRef}
                    id="dropzone-file"
                    type="file"
                    className="hidden"
                    onChange={handleImageUpload}
                    accept="image/png, image/jpeg, image/gif"
                    multiple
                    required
                  />
                </label>

                {imagePreviews.length > 0 && (
                  <div ref={previewRef} className="mt-5">
                    <div className="overflow-hidden rounded-[20px] border border-[#d4af37]/20 bg-white p-3 shadow-[0_15px_35px_rgba(43,0,0,0.08)]">
                      <div className="grid grid-cols-2 gap-3">
                        {imagePreviews.map((src, idx) => (
                          <div key={src} className="group relative overflow-hidden rounded-xl border border-[#e6d7ca] bg-white">
                            <img src={src} alt={`Uploaded Preview ${idx + 1}`} className="h-32 w-full bg-[#fffaf5] object-contain transition duration-500 group-hover:scale-105" />
                            <button
                              type="button"
                              onClick={() => removeOneImage(idx)}
                              className="absolute right-2 top-2 rounded-full border border-[#d4af37]/25 bg-white/95 px-2 py-1 text-xs text-[#2B0000] shadow-sm transition hover:bg-[#2B0000] hover:text-white"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mt-3 flex items-center justify-between text-xs text-[#6f5e57]">
                      <span className="truncate">
                        {imageFiles.length} file(s) selected
                      </span>
                      <button
                        type="button"
                        onClick={removeAllImages}
                        className="rounded-full border border-[#d4af37]/25 px-3 py-1 transition hover:bg-[#2B0000] hover:text-white"
                      >
                        Remove All
                      </button>
                    </div>
                  </div>
                )}

                {/* Quick checklist */}
                <div className="mt-6 rounded-[20px] border border-[#d4af37]/20 bg-[linear-gradient(145deg,#fffaf5,#f4e7da)] p-4">
                  <h3 className="text-sm font-semibold text-[#2B0000]">Checklist</h3>
                  <ul className="mt-3 space-y-2 text-sm text-[#4a3732]">
                    <li className="flex items-center justify-between">
                      <span>Base fields</span>
                      <span className={requiredBaseFieldsValid ? "text-emerald-700 font-semibold" : "text-amber-700 font-semibold"}>
                        {requiredBaseFieldsValid ? "OK" : "Pending"}
                      </span>
                    </li>
                    <li className="flex items-center justify-between">
                      <span>Selected sizes</span>
                      <span className={selectedSizes.length ? "text-emerald-700 font-semibold" : "text-amber-700 font-semibold"}>
                        {selectedSizes.length ? `${selectedSizes.length}` : "0"}
                      </span>
                    </li>
                    <li className="flex items-center justify-between">
                      <span>Images</span>
                      <span className={imageFiles.length ? "text-emerald-700 font-semibold" : "text-amber-700 font-semibold"}>
                        {imageFiles.length ? "OK" : "Missing"}
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Secondary submit button */}
                <button
                  type="submit"
                  disabled={submitting}
                  className={[
                    "mt-5 inline-flex w-full items-center justify-center rounded-xl px-5 py-3.5 text-sm font-semibold transition shadow-[0_12px_30px_rgba(43,0,0,0.18)]",
                    submitting ? "bg-[#8d7d75] text-white cursor-not-allowed" : "bg-[#2B0000] text-[#F7E5B3] hover:bg-[#4A090B] hover:-translate-y-0.5 active:scale-[0.99]",
                  ].join(" ")}
                >
                  {submitting ? "Saving..." : "Add Product"}
                </button>
              </div>
            </section>
          </div>
        </div>
      </form>

      <style>{`
        .add-product-luxury {
          background:
            radial-gradient(circle at 8% 2%, rgba(212,175,55,.12), transparent 28%),
            radial-gradient(circle at 94% 82%, rgba(43,0,0,.07), transparent 30%),
            #F7EFE7;
        }

        .add-product-luxury input,
        .add-product-luxury select,
        .add-product-luxury textarea,
        .add-product-luxury button {
          font: inherit;
        }

        .add-product-luxury input[type="checkbox"] {
          cursor: pointer;
        }

        .add-product-luxury input[type="datetime-local"] {
          color-scheme: light;
        }

        .add-product-luxury textarea {
          resize: vertical;
          min-height: 120px;
        }

        .add-product-luxury table {
          border-collapse: separate;
          border-spacing: 0;
        }

        .add-product-luxury tbody tr {
          transition: background-color .2s ease;
        }

        .add-product-luxury ::selection {
          color: #F7E5B3;
          background: #2B0000;
        }

        .add-product-luxury * {
          scrollbar-width: thin;
          scrollbar-color: rgba(43,0,0,.35) rgba(212,175,55,.08);
        }

        .add-product-luxury *::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .add-product-luxury *::-webkit-scrollbar-track {
          background: rgba(212,175,55,.08);
        }

        .add-product-luxury *::-webkit-scrollbar-thumb {
          border: 2px solid transparent;
          border-radius: 999px;
          background: rgba(43,0,0,.35);
          background-clip: padding-box;
        }

        @media (max-width: 640px) {
          .add-product-luxury .toast-anim {
            right: 12px;
            bottom: 12px;
            left: 12px;
          }

          .add-product-luxury section {
            border-radius: 18px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .add-product-luxury *,
          .add-product-luxury *::before,
          .add-product-luxury *::after {
            scroll-behavior: auto !important;
            transition-duration: .01ms !important;
            animation-duration: .01ms !important;
            animation-iteration-count: 1 !important;
          }
        }
      `}</style>
    </div>
  );
}

export default AddProduct;